import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../auth";
import { uploadDocuments, listDocuments, deleteDocument } from "../api";
import {
  BatchDeleteResult,
  BatchUploadResult,
  DeleteProgress,
  Document,
} from "../types";

export function useDocuments() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingAnnotations, setIsUploadingAnnotations] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<DeleteProgress | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [lastUploadResult, setLastUploadResult] = useState<BatchUploadResult | null>(null);
  const pendingAnnotationIdsRef = useRef<Set<string>>(new Set());

  const loadDocuments = useCallback(async () => {
    try {
      const previousPendingIds = pendingAnnotationIdsRef.current;
      const docs = await listDocuments();

      const newlyProcessed = docs.filter(
        (d) =>
          previousPendingIds.has(d.id) &&
          d.status === "processed" &&
          d.document_type === "UPLOADED_EVALUATION"
      );
      if (newlyProcessed.length > 0) {
        toast.success("Your annotation(s) are ready.");
      }

      pendingAnnotationIdsRef.current = new Set(
        docs
          .filter(
            (d) =>
              d.status === "pending" &&
              d.document_type === "UPLOADED_EVALUATION"
          )
          .map((d) => d.id)
      );

      setDocuments(docs);
      setError(null);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load documents";
      toast.error(msg);
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || authLoading) {
      return;
    }
    setIsLoading(true);
    loadDocuments();
  }, [isAuthenticated, authLoading, loadDocuments]);

  const onUpload = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      setIsUploading(true);
      try {
        const result = await uploadDocuments(files);
        setLastUploadResult(result);
        await loadDocuments();
        const uploadedCount = result.uploaded.length;
        const skippedCount = result.skipped.length;
        if (uploadedCount > 0) {
          toast.success(`Uploaded ${uploadedCount} document(s)`);
        }
        if (skippedCount > 0) {
          toast.info(`Skipped ${skippedCount} (duplicate content)`);
        }
        if (uploadedCount === 0 && skippedCount > 0) {
          toast.info("All files skipped (duplicate content)");
        }
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : "Failed to upload documents";
        const axiosErr = err as { response?: { status?: number; data?: { detail?: string } } };
        const detail = axiosErr.response?.data?.detail;
        const finalMsg = detail || msg;
        toast.error(finalMsg);
        throw err;
      } finally {
        setIsUploading(false);
      }
    },
    [loadDocuments]
  );

  const onUploadAnnotations = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      setIsUploadingAnnotations(true);
      try {
        const result = await uploadDocuments(files, "UPLOADED_EVALUATION");
        setLastUploadResult(result);
        await loadDocuments();
        const uploadedCount = result.uploaded.length;
        const skippedCount = result.skipped.length;
        if (uploadedCount > 0) {
          toast.success(`Uploaded ${uploadedCount} annotation(s)`);
        }
        if (skippedCount > 0) {
          toast.info(`Skipped ${skippedCount} (duplicate content)`);
        }
        if (uploadedCount === 0 && skippedCount > 0) {
          toast.info("All files skipped (duplicate content)");
        }
      } catch (err: unknown) {
        const msg =
          err instanceof Error ? err.message : "Failed to upload annotations";
        const axiosErr = err as { response?: { status?: number; data?: { detail?: string } } };
        const detail = axiosErr.response?.data?.detail;
        const finalMsg = detail || msg;
        toast.error(finalMsg);
        throw err;
      } finally {
        setIsUploadingAnnotations(false);
      }
    },
    [loadDocuments]
  );

  const onSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      await loadDocuments();
    } catch {
      // silently ignore
    } finally {
      setIsSyncing(false);
    }
  }, [loadDocuments]);

  /**
   * Delete documents one at a time, reporting what survived.
   *
   * The API only deletes a single document per call, and each refusal is that
   * document's own (409 while its evaluations are in use, 503 while LlamaCloud
   * is unreachable), so the fan-out happens here and every reason stays
   * attached to the file it belongs to - the same shape `uploadDocuments`
   * already returns for a batch upload. Sequential, because a delete reaches
   * LlamaCloud, Postgres and S3, and "Deleting 3 of 12" should mean it.
   */
  const onDelete = useCallback(
    async (docs: Document[]): Promise<BatchDeleteResult> => {
      const result: BatchDeleteResult = { deleted: [], failed: [] };
      setDeleteProgress({ completed: 0, total: docs.length });
      try {
        for (const doc of docs) {
          try {
            await deleteDocument(doc.id);
            result.deleted.push(doc.id);
          } catch (err: unknown) {
            // The API refuses rather than half-deleting, so the document is
            // still there and the reason is worth showing verbatim.
            const reason =
              err instanceof Error ? err.message : "Failed to delete document";
            result.failed.push({
              documentId: doc.id,
              filename: doc.filename,
              reason,
            });
          }
          setDeleteProgress({
            completed: result.deleted.length + result.failed.length,
            total: docs.length,
          });
        }

        if (result.deleted.length > 0) {
          await loadDocuments();
          toast.success(
            result.deleted.length === 1
              ? "Document deleted"
              : `Deleted ${result.deleted.length} documents`
          );
        }
        if (result.failed.length > 0) {
          // The toast counts; the reasons themselves are listed in the dialog,
          // where they stay put instead of timing out.
          toast.error(
            result.failed.length === 1
              ? "1 document could not be deleted"
              : `${result.failed.length} documents could not be deleted`
          );
        }
      } finally {
        setDeleteProgress(null);
      }
      return result;
    },
    [loadDocuments]
  );

  const clearUploadResult = useCallback(() => {
    setLastUploadResult(null);
  }, []);

  return {
    documents,
    isLoading: isLoading || authLoading,
    isUploading,
    isUploadingAnnotations,
    isSyncing,
    deleteProgress,
    error,
    lastUploadResult,
    onUpload,
    onUploadAnnotations,
    onSync,
    onDelete,
    clearUploadResult,
    refresh: loadDocuments,
  };
}
